import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import CheckoutShippingAddress from '../../components/checkout/CheckoutShippingAddress';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Separator } from '../../components/ui/separator';
import { 
  Storefront, 
  CreditCard, 
  Truck,
  MapPin,
  Package,
  ArrowLeft,
  Check,
  ShoppingCart,
  Ticket,
  CircleNotch,
  Gift,
  Clock,
  Warning,
  House, Phone, Envelope, User, Heart, List, X, Tag,
  FacebookLogo, InstagramLogo, TiktokLogo
} from '@phosphor-icons/react';
import axios from 'axios';
import { useCart } from '../../context/CartContext';
import PaypalPayLater from '../../components/PaypalPayLater';
import { toast } from 'sonner';
import { Progress } from '../../components/ui/progress';
import { MrgHeader as SharedMagereHeader } from './MrgHeader';
import { MrgFooter as SharedMagereFooter } from './MrgFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_STORE_SLUG = "mrg";

const CheckoutPage = ({ storeSlug: propStoreSlug }) => {
  const { storeSlug: paramStoreSlug } = useParams();
  const storeSlug = propStoreSlug || paramStoreSlug || DEFAULT_STORE_SLUG;
  const standalone = !!propStoreSlug || !paramStoreSlug;
  const navigate = useNavigate();
  const { items, total, clearCart, itemCount, setIsOpen, taxBreakdown } = useCart();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  // Helper for standalone links
  const getLink = (path) => standalone ? path : `/store/${storeSlug}${path}`;
  
  const [shippingAddress, setShippingAddress] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'FR'
  });
  
  const [shippingMethod, setShippingMethod] = useState('');
  const [shippingOptions, setShippingOptions] = useState([]);
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [shippingCost, setShippingCost] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [pointRelay, setPointRelay] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [template, setTemplate] = useState(null);
  const [preparationInfo, setPreparationInfo] = useState(null);
  const [minOrderIssues, setMinOrderIssues] = useState([]);
  const [loyaltyDiscount, setLoyaltyDiscount] = useState({ discount_percent: 0, tier_name: null });
  const [deliveryToOtherAddress, setDeliveryToOtherAddress] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState({
    address: '', city: '', postal_code: '', country: 'FR'
  });
  const [deliveryCitySuggestions, setDeliveryCitySuggestions] = useState([]);
  const [loadingDeliveryCity, setLoadingDeliveryCity] = useState(false);
  const [deliveryCitySearchResults, setDeliveryCitySearchResults] = useState([]);
  const [showDeliveryCityDropdown, setShowDeliveryCityDropdown] = useState(false);
  const [skipDeliveryCityLookup, setSkipDeliveryCityLookup] = useState(false);
  const [skipDeliveryPostalLookup, setSkipDeliveryPostalLookup] = useState(false);

  // Lookup city from postal code for delivery address
  useEffect(() => {
    if (!deliveryToOtherAddress || skipDeliveryCityLookup || !deliveryAddress.postal_code || deliveryAddress.postal_code.length < 5) {
      setDeliveryCitySuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoadingDeliveryCity(true);
      try {
        const res = await fetch(`https://geo.api.gouv.fr/communes?codePostal=${deliveryAddress.postal_code}&fields=nom,codesPostaux&format=json`);
        const data = await res.json();
        const cities = (data || []).map(c => c.nom);
        setDeliveryCitySuggestions(cities);
        if (cities.length === 1) {
          setSkipDeliveryPostalLookup(true);
          setDeliveryAddress(prev => (prev.city === cities[0] ? prev : { ...prev, city: cities[0] }));
        } else if (cities.length > 1 && !cities.includes(deliveryAddress.city)) {
          setSkipDeliveryPostalLookup(true);
          setDeliveryAddress(prev => (prev.city === '' ? prev : { ...prev, city: '' }));
        }
      } catch { setDeliveryCitySuggestions([]); }
      finally { setLoadingDeliveryCity(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [deliveryAddress.postal_code, deliveryToOtherAddress, skipDeliveryCityLookup]);

  // Lookup postal from city name for delivery address
  useEffect(() => {
    if (!deliveryToOtherAddress || skipDeliveryPostalLookup || !deliveryAddress.city || deliveryAddress.city.length < 2) {
      setDeliveryCitySearchResults([]);
      setShowDeliveryCityDropdown(false);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(deliveryAddress.city)}&fields=nom,codesPostaux&limit=8&boost=population&format=json`);
        const data = await res.json();
        const results = (data || []).map(c => ({ name: c.nom, postalCode: c.codesPostaux?.[0] || '' }))
          .sort((a, b) => {
            const aExact = a.name.toLowerCase() === deliveryAddress.city.toLowerCase() ? 0 : 1;
            const bExact = b.name.toLowerCase() === deliveryAddress.city.toLowerCase() ? 0 : 1;
            return aExact - bExact;
          });
        setDeliveryCitySearchResults(results);
        setShowDeliveryCityDropdown(results.length > 0);
      } catch { setDeliveryCitySearchResults([]); setShowDeliveryCityDropdown(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [deliveryAddress.city, deliveryToOtherAddress, skipDeliveryPostalLookup]);

  const selectDeliveryCity = (result) => {
    setSkipDeliveryCityLookup(true);
    setSkipDeliveryPostalLookup(true);
    setDeliveryAddress(prev => ({ ...prev, city: result.name, postal_code: result.postalCode }));
    setShowDeliveryCityDropdown(false);
    setDeliveryCitySearchResults([]);
  };

  useEffect(() => {
    const fetchStore = async () => {
      try {
        const [storeRes, templateRes] = await Promise.all([
          axios.get(`${API_URL}/api/s/${storeSlug}`),
          axios.get(`${API_URL}/api/s/${storeSlug}/template`),
        ]);
        setStore(storeRes.data);
        setTemplate(templateRes.data);
      } catch (error) {
        console.error('Store not found:', error);
      }
    };

    if (storeSlug) fetchStore();
  }, [storeSlug]);

  /* ── Fetch shipping options when moving to step 2 ── */
  const fetchShippingOptions = async () => {
    setLoadingShipping(true);
    try {
      const cartWeight = items.reduce((acc, item) => {
        // Use the product's actual weight (kg) field, default 0.4 kg (400g for a pair of glasses)
        const w = (typeof item.weight === 'number' && item.weight > 0) ? item.weight : 0.4;
        return acc + w * item.quantity;
      }, 0);

      // Use delivery address if different, otherwise shipping address
      const targetCity = deliveryToOtherAddress ? deliveryAddress.city : shippingAddress.city;
      const targetPostal = deliveryToOtherAddress ? deliveryAddress.postal_code : shippingAddress.postal_code;
      
      const resp = await axios.post(`${API_URL}/api/s/${storeSlug}/calculate-shipping`, {
        postal_code: targetPostal,
        city: targetCity,
        country: shippingAddress.country,
        cart_total: total,
        cart_weight: cartWeight
      });
      setShippingOptions(resp.data.methods || []);
      setPreparationInfo(resp.data.preparation || null);
      setMinOrderIssues(resp.data.min_order_issues || []);
      if (resp.data.methods?.length > 0) {
        const first = resp.data.methods[0];
        setSelectedOption(first);
        setShippingMethod(first.carrier);
        setShippingCost(first.price);
      }
    } catch (err) {
      console.error('Shipping calc error:', err);
      setShippingOptions([]);
    } finally {
      setLoadingShipping(false);
    }
  };

  const formatDateFr = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const months = ['janvier', 'fevrier', 'mars', 'avril', 'mai', 'juin', 'juillet', 'aout', 'septembre', 'octobre', 'novembre', 'decembre'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  };

  useEffect(() => {
    // Load Mondial Relay widget
    if (shippingMethod === 'mondial_relay' && store?.settings?.enable_mondial_relay) {
      const loadMondialRelay = () => {
        if (window.$ && window.$.fn.MR_ParcelShopPicker) {
          window.$("#Zone_Widget").MR_ParcelShopPicker({
            Target: "#Target_Widget",
            Brand: store.settings.mondial_relay_brand || "BDTEST  ",
            Country: "FR",
            PostCode: shippingAddress.postal_code || "75001",
            ColLivMod: "24R",
            NbResults: "7",
            Responsive: true,
            ShowResultsOnMap: true,
            OnParcelShopSelected: (data) => {
              setPointRelay({
                id: data.ID,
                name: data.Nom,
                address: `${data.Adresse1} ${data.Adresse2}`.trim(),
                city: data.Ville,
                postal_code: data.CP,
                country: data.Pays
              });
            }
          });
        }
      };

      // Load jQuery and Mondial Relay scripts
      if (!window.$) {
        const jqueryScript = document.createElement('script');
        jqueryScript.src = "https://ajax.googleapis.com/ajax/libs/jquery/2.2.4/jquery.min.js";
        jqueryScript.onload = () => {
          const leafletCss = document.createElement('link');
          leafletCss.rel = 'stylesheet';
          leafletCss.href = 'https://unpkg.com/leaflet/dist/leaflet.css';
          document.head.appendChild(leafletCss);

          const leafletScript = document.createElement('script');
          leafletScript.src = "https://unpkg.com/leaflet/dist/leaflet.js";
          leafletScript.onload = () => {
            const mrScript = document.createElement('script');
            mrScript.src = "https://widget.mondialrelay.com/parcelshop-picker/jquery.plugin.mondialrelay.parcelshoppicker.min.js";
            mrScript.onload = loadMondialRelay;
            document.body.appendChild(mrScript);
          };
          document.body.appendChild(leafletScript);
        };
        document.body.appendChild(jqueryScript);
      } else {
        loadMondialRelay();
      }
    }
  }, [shippingMethod, store, shippingAddress.postal_code]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setShippingAddress(prev => ({ ...prev, [name]: value }));
    if (name === 'email' && value.includes('@')) {
      axios.get(`${API_URL}/api/s/${storeSlug}/loyalty/tier-discount?email=${encodeURIComponent(value)}`)
        .then(r => setLoyaltyDiscount(r.data))
        .catch(() => {});
    }
  };

  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    setValidatingPromo(true);
    try {
      const resp = await axios.post(`${API_URL}/api/s/${storeSlug}/validate-promo`, {
        code: promoCode.trim(),
        cart_total: total
      });
      setPromoApplied(resp.data);
      toast.success(`Code promo appliqué : -${resp.data.discount_amount.toFixed(2)} €`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code promo invalide');
      setPromoApplied(null);
    } finally { setValidatingPromo(false); }
  };

  const removePromo = () => { setPromoApplied(null); setPromoCode(''); };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const orderData = {
        items: items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          variation: item.variation
        })),
        shipping_method: shippingMethod,
        shipping_address: shippingAddress,
        delivery_address: deliveryToOtherAddress ? deliveryAddress : null,
        payment_method: paymentMethod,
        shipping_point_relay: pointRelay,
        delivery_date: selectedDate || null,
        delivery_slot: selectedSlot || null,
        shipping_cost: shippingCost,
        shipping_option_name: selectedOption?.name || '',
        promo_code: promoApplied?.code || null,
        discount_amount: promoApplied?.discount_amount || 0
      };

      // Create order first
      const response = await axios.post(`${API_URL}/api/s/${storeSlug}/orders`, orderData);
      
      if (paymentMethod === 'stripe') {
        // Create Stripe checkout session
        try {
          const stripeResp = await axios.post(`${API_URL}/api/s/${storeSlug}/checkout/stripe`, {
            order_id: response.data.id,
            origin_url: window.location.origin
          });
          
          // Redirect to Stripe hosted checkout
          if (stripeResp.data.url) {
            window.location.href = stripeResp.data.url;
            return; // Don't clear cart yet — will clear on return
          }
        } catch (stripeErr) {
          console.error('Stripe checkout error:', stripeErr);
          // Stripe not configured — fallback to confirmation
          clearCart();
          toast.success('Commande passée avec succès ! (Paiement en attente)');
          navigate(getLink(`/confirmation?order=${response.data.order_number}&tracking=${response.data.tracking_code}`));
        }
      } else if (paymentMethod === 'paypal') {
        // Create PayPal checkout
        try {
          const paypalResp = await axios.post(`${API_URL}/api/s/${storeSlug}/checkout/paypal`, {
            order_id: response.data.id,
            origin_url: window.location.origin
          });
          
          if (paypalResp.data.url) {
            window.location.href = paypalResp.data.url;
            return; // Don't clear cart yet — will clear on return
          }
        } catch (paypalErr) {
          console.error('PayPal checkout error:', paypalErr);
          clearCart();
          toast.success('Commande passée avec succès ! (Paiement en attente)');
          navigate(getLink(`/confirmation?order=${response.data.order_number}&tracking=${response.data.tracking_code}`));
        }
      } else {
        // COD or pickup — direct confirmation
        clearCart();
        toast.success('Commande passée avec succès !');
        navigate(getLink(`/confirmation?order=${response.data.order_number}&tracking=${response.data.tracking_code}`));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la commande');
    } finally {
      setLoading(false);
    }
  };

  const tc = template?.colors || {};
  const si = store?.identity || {};
  const clr = {
    primary: tc.primary || si.primary_color || '#1a3d5c',
    secondary: tc.secondary || si.secondary_color || '#0cb5c6',
    accent: tc.accent || si.accent_color || '#e0a800',
  };

  const isMagere = store?.identity?.theme_template === 'magere' || storeSlug === 'mrg' || storeSlug === 'magere-viandes' || standalone;
  const logoUrl = store?.identity?.logo_light || '';

  const MagereCheckoutHeader = () => <SharedMagereHeader store={store} standalone={standalone} />;
  const MagereCheckoutFooter = () => <SharedMagereFooter store={store} standalone={standalone} />;

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex flex-col overflow-x-hidden" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <link href="https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700;900&display=swap" rel="stylesheet" />
        {isMagere ? <MagereCheckoutHeader /> : (
          <header className="sticky top-0 z-50">
            <nav style={{ backgroundColor: clr.primary }}>
              <div className="max-w-[1200px] mx-auto px-4 flex items-center justify-between h-[56px]">
                <Link to={getLink('/')} className="flex items-center gap-2 text-white">
                  <span className="text-lg font-extrabold tracking-tight">{store?.name || 'MAGERE'}</span>
                </Link>
                <span className="text-sm text-white/60">Paiement sécurisé</span>
              </div>
            </nav>
          </header>
        )}
        {isMagere && <div className="h-[50px]" />}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <ShoppingCart size={64} className="mx-auto text-slate-300 mb-4" />
            <h1 className="text-2xl font-bold mb-4">Votre panier est vide</h1>
            <Link to={getLink('/')}>
              <Button style={{ backgroundColor: clr.secondary }} className="text-white font-bold">
                Continuer mes achats
              </Button>
            </Link>
          </div>
        </div>
        {isMagere && <MagereCheckoutFooter />}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb] flex flex-col overflow-x-hidden" data-testid="checkout-page" style={{ '--clr-primary': clr.primary, '--clr-secondary': clr.secondary, '--clr-accent': clr.accent }}>
      <link href="https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700;900&display=swap" rel="stylesheet" />
      <style>{`
        [data-testid="checkout-page"] .btn-primary { background-color: var(--clr-secondary) !important; }
        [data-testid="checkout-page"] .btn-primary:hover { filter: brightness(0.9); }
        [data-testid="checkout-page"] .icon-primary { color: var(--clr-secondary); }
        [data-testid="checkout-page"] .step-active { background-color: var(--clr-secondary) !important; color: white !important; }
        [data-testid="checkout-page"] .hover-border:hover { border-color: var(--clr-secondary) !important; }
      `}</style>
      {isMagere ? <MagereCheckoutHeader /> : (
        <header className="sticky top-0 z-50">
          <nav style={{ backgroundColor: clr.primary }}>
            <div className="max-w-[1200px] mx-auto px-4 flex items-center justify-between h-[56px]">
              <Link to={getLink('/')} className="flex items-center gap-2 text-white">
                <span className="text-lg font-extrabold tracking-tight">{store?.name || 'MAGERE'}</span>
              </Link>
              <span className="text-sm text-white/60">Paiement sécurisé</span>
            </div>
          </nav>
        </header>
      )}
      <div className={isMagere ? 'h-[56px]' : ''} />

      <main className="flex-1">
      <div className="max-w-[1200px] mx-auto px-4 py-8">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6"
        >
          <ArrowLeft size={18} />
          Retour
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Progress */}
            <div className="flex items-center gap-4 mb-8">
              {[
                { num: 1, label: 'Livraison' },
                { num: 2, label: 'Expédition' },
                { num: 3, label: 'Paiement' }
              ].map((s, idx) => (
                <React.Fragment key={s.num}>
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step >= s.num ? 'step-active' : 'bg-slate-200 text-slate-500'
                    }`}>
                      {step > s.num ? <Check size={16} /> : s.num}
                    </div>
                    <span className={step >= s.num ? 'font-medium' : 'text-slate-500'}>{s.label}</span>
                  </div>
                  {idx < 2 && <div className="flex-1 h-0.5 bg-slate-200" />}
                </React.Fragment>
              ))}
            </div>

            {/* Step 1: Shipping Address */}
            {step === 1 && (
              <CheckoutShippingAddress
                shippingAddress={shippingAddress}
                handleInputChange={handleInputChange}
                onContinue={() => { fetchShippingOptions(); setStep(2); }}
              />
            )}

            {/* Step 2: Shipping Method (Dynamic) */}
            {step === 2 && (
              <Card className="dashboard-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Truck size={20} className="icon-primary" />
                    Mode de livraison
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Delivery address info + toggle */}
                  <div className="p-3 bg-slate-50 rounded-lg" data-testid="delivery-address-info">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <MapPin size={16} className="text-slate-500" />
                        <span className="text-sm font-medium text-slate-700">
                          Adresse de livraison : <strong>{deliveryToOtherAddress ? deliveryAddress.city || '...' : shippingAddress.city || '...'}</strong> ({deliveryToOtherAddress ? deliveryAddress.postal_code : shippingAddress.postal_code})
                        </span>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input type="checkbox" checked={deliveryToOtherAddress}
                        onChange={e => {
                          setDeliveryToOtherAddress(e.target.checked);
                          if (!e.target.checked) {
                            // Reset to original address
                            setShippingOptions([]);
                            setSelectedOption(null);
                            fetchShippingOptions();
                          } else {
                            // Clear shipping results until user verifies new address
                            setShippingOptions([]);
                            setSelectedOption(null);
                          }
                        }}
                        className="rounded border-slate-300"
                        data-testid="other-address-toggle" />
                      <span className="text-sm text-slate-600">Livrer à une autre adresse</span>
                    </label>
                    {deliveryToOtherAddress && (
                      <div className="mt-3 space-y-3 pl-6 border-l-2 border-slate-200">
                        <div className="space-y-1.5">
                          <Label className="text-xs">Adresse de livraison *</Label>
                          <Input placeholder="Rue, numéro..." value={deliveryAddress.address}
                            onChange={e => setDeliveryAddress(prev => ({...prev, address: e.target.value}))}
                            data-testid="delivery-address-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <Label className="text-xs">Code postal</Label>
                            <Input placeholder="13800" value={deliveryAddress.postal_code}
                              onChange={e => { setSkipDeliveryPostalLookup(true); setSkipDeliveryCityLookup(false); setDeliveryAddress(prev => ({...prev, postal_code: e.target.value})); }}
                              data-testid="delivery-postal-input" />
                          </div>
                          <div className="space-y-1.5 relative">
                            <Label className="text-xs">Ville *</Label>
                            {loadingDeliveryCity ? (
                              <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-slate-50 text-xs text-slate-400">
                                <CircleNotch size={12} className="animate-spin" /> Recherche...
                              </div>
                            ) : deliveryCitySuggestions.length > 1 ? (
                              <select value={deliveryAddress.city}
                                onChange={e => { setSkipDeliveryPostalLookup(true); setDeliveryAddress(prev => ({...prev, city: e.target.value})); }}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                data-testid="delivery-city-input">
                                <option value="">Sélectionner</option>
                                {deliveryCitySuggestions.map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                            ) : (
                              <>
                                <Input placeholder="Istres, Salon..." value={deliveryAddress.city}
                                  onChange={e => { setSkipDeliveryCityLookup(true); setSkipDeliveryPostalLookup(false); setDeliveryAddress(prev => ({...prev, city: e.target.value})); }}
                                  onFocus={() => { if (deliveryCitySearchResults.length > 0) setShowDeliveryCityDropdown(true); }}
                                  onBlur={() => setTimeout(() => setShowDeliveryCityDropdown(false), 200)}
                                  autoComplete="off"
                                  data-testid="delivery-city-input" />
                                {showDeliveryCityDropdown && deliveryCitySearchResults.length > 0 && (
                                  <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-[200px] overflow-y-auto">
                                    {deliveryCitySearchResults.map((r, i) => (
                                      <button key={i} type="button"
                                        onMouseDown={e => { e.preventDefault(); selectDeliveryCity(r); }}
                                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-slate-50 flex items-center justify-between border-b border-slate-50 last:border-0">
                                        <span className="font-medium text-slate-800">{r.name}</span>
                                        <span className="text-xs text-slate-400">{r.postalCode}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <Button size="sm" variant="outline" className="text-sm"
                          disabled={!deliveryAddress.city}
                          onClick={() => { setShippingOptions([]); setSelectedOption(null); fetchShippingOptions(); }}
                          data-testid="recalculate-shipping-btn">
                          <Truck size={14} className="mr-1.5" /> Vérifier la disponibilité
                        </Button>
                      </div>
                    )}
                  </div>

                  {loadingShipping ? (
                    <div className="flex items-center justify-center py-10 gap-3">
                      <CircleNotch size={24} className="animate-spin icon-primary" />
                      <span className="text-slate-500">Calcul des options de livraison...</span>
                    </div>
                  ) : (
                    <>
                      {/* Min order warnings */}
                      {minOrderIssues.length > 0 && (
                        <div className="space-y-2" data-testid="min-order-warnings">
                          {minOrderIssues.map((issue, idx) => (
                            <div key={idx} className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                              <Warning size={20} className="text-amber-500 flex-shrink-0 mt-0.5" weight="fill" />
                              <div>
                                <p className="font-semibold text-amber-800 text-sm">{issue.rule_name}</p>
                                <p className="text-amber-700 text-sm">
                                  Montant minimum requis : <strong>{issue.min_order.toFixed(2)} €</strong>
                                </p>
                                <p className="text-amber-600 text-xs mt-1">
                                  Il vous manque <strong>{issue.missing.toFixed(2)} €</strong> pour débloquer cette option de livraison.
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {shippingOptions.length === 0 && minOrderIssues.length === 0 && (
                        <div className="text-center py-8" data-testid="no-shipping-available">
                          <Truck size={40} className="mx-auto text-slate-300 mb-3" />
                          <p className="text-[16px] font-semibold text-slate-600 mb-2">Indisponible actuellement</p>
                          <p className="text-sm text-slate-400">La livraison n'est pas disponible pour votre adresse.</p>
                          <p className="text-sm text-slate-400 mt-1">Pour toute demande, <a href={`tel:${store?.identity?.contact_phone?.replace(/\s/g, '') || ''}`} className="font-medium underline" style={{ color: clr.primary }}>contactez-nous</a>.</p>
                        </div>
                      )}

                      {/* Preparation info */}
                      {preparationInfo && shippingOptions.length > 0 && (
                        <div className="flex items-center gap-2.5 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800" data-testid="preparation-info">
                          <Clock size={18} className="flex-shrink-0 text-blue-500" weight="fill" />
                          <span>{preparationInfo.message}</span>
                        </div>
                      )}

                      {shippingOptions.length > 0 && (
                        <div className="space-y-3">
                          {/* Free shipping gauge — show progress to next free tier */}
                          {(() => {
                            const deliveryOpts = shippingOptions.filter(o => o.rule_type === 'delivery' && o.free_above);
                            if (!deliveryOpts.length) return null;
                            const threshold = Math.min(...deliveryOpts.map(o => o.free_above));
                            const refOpt = deliveryOpts.find(o => o.free_above === threshold);
                            const standardPrice = refOpt?.original_price ?? refOpt?.price ?? 0;
                            const remaining = Math.max(0, threshold - total);
                            const pct = Math.min(100, Math.round((total / threshold) * 100));
                            const reached = remaining === 0;
                            return (
                              <div className={`p-3 rounded-xl border-2 ${reached ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`} data-testid="free-shipping-gauge">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2 text-[13px] font-semibold">
                                    {reached
                                      ? <span className="text-emerald-700">🎉 Livraison gratuite débloquée — vous économisez {standardPrice.toFixed(2)} €</span>
                                      : <span className="text-slate-700">
                                          Plus que <span className="font-bold" style={{ color: clr.primary }}>{remaining.toFixed(2)} €</span> pour la livraison gratuite
                                          {standardPrice > 0 && <span className="text-slate-500"> · économisez {standardPrice.toFixed(2)} €</span>}
                                        </span>}
                                  </div>
                                </div>
                                <div className="w-full h-2 rounded-full bg-white overflow-hidden border border-slate-100">
                                  <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: reached ? '#10b981' : clr.primary }} />
                                </div>
                                {!reached && (
                                  <p className="text-[11px] text-slate-500 mt-1.5">Ajoutez {remaining.toFixed(2)} € à votre panier pour bénéficier de la livraison offerte ({threshold.toFixed(0)} € minimum).</p>
                                )}
                              </div>
                            );
                          })()}

                          {shippingOptions.map(opt => {
                            const isSelected = selectedOption?.id === opt.id;
                            const isFree = opt.price === 0;
                            const dates = opt.available_dates || [];
                            return (
                              <div key={opt.id}
                                onClick={() => { setSelectedOption(opt); setShippingMethod(opt.carrier); setShippingCost(opt.price); setSelectedDate(''); setSelectedSlot(''); }}
                                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${isSelected ? 'border-current shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}
                                style={isSelected ? { borderColor: clr.primary } : {}}
                                data-testid={`shipping-option-${opt.rule_type}`}>
                                
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? '' : 'border-slate-300'}`}
                                      style={isSelected ? { borderColor: clr.primary } : {}}>
                                      {isSelected && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: clr.primary }} />}
                                    </div>
                                    <div>
                                      <div className="flex items-center gap-2">
                                        {opt.rule_type === 'pickup' && <Storefront size={18} className="icon-primary" />}
                                        {opt.rule_type === 'delivery' && <Truck size={18} className="icon-primary" />}
                                        {opt.rule_type === 'expedition' && <Package size={18} className="icon-primary" />}
                                        <span className="font-semibold text-[15px]">
                                          {opt.rule_type === 'pickup'
                                            ? (opt.name || 'Retrait en boutique')
                                            : opt.rule_type === 'expedition'
                                              ? (opt.name || 'Expédition')
                                              : `Livraison à ${deliveryToOtherAddress ? (deliveryAddress.city || '...') : (shippingAddress.city || '...')}`}
                                        </span>
                                      </div>
                                      {opt.rule_type === 'delivery' && (
                                        <p className="text-[11px] text-slate-400 ml-[26px]">
                                          → {deliveryToOtherAddress
                                              ? `${deliveryAddress.address || ''}, ${deliveryAddress.postal_code} ${deliveryAddress.city}`
                                              : `${shippingAddress.address || ''}, ${shippingAddress.postal_code} ${shippingAddress.city}`}
                                        </p>
                                      )}
                                      {opt.delivery_days?.length > 0 && (
                                        <p className="text-[12px] text-slate-500 ml-[26px]">{opt.delivery_days.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}{opt.estimated_days ? ` — ${opt.estimated_days}` : ''}</p>
                                      )}
                                      {opt.delivery_note && (
                                        <p className="text-[11px] text-amber-600 ml-[26px] italic">{opt.delivery_note}</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    {isFree ? (
                                      <span className="font-bold text-emerald-600 text-[15px]">Gratuit</span>
                                    ) : (
                                      <span className="font-bold text-[15px]">{opt.price.toFixed(2)} €</span>
                                    )}
                                    {opt.free_above && !isFree && (
                                      <p className="text-[11px] text-emerald-500">Gratuit dès {opt.free_above} €</p>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Pickup address */}
                                {opt.rule_type === 'pickup' && opt.pickup_address && isSelected && (
                                  <div className="ml-8 mt-3 p-3 bg-slate-50 rounded-lg">
                                    <div className="flex items-start gap-2 text-[13px]">
                                      <MapPin size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                                      <div>
                                        <p className="font-medium text-slate-700">{opt.pickup_address}</p>
                                        {opt.pickup_phone && <p className="text-slate-400 mt-0.5">{opt.pickup_phone}</p>}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Date & Slot selection */}
                                {isSelected && dates.length > 0 && (
                                  <div className="ml-8 mt-3 space-y-3">
                                    <div>
                                      <p className="text-[12px] font-semibold text-slate-600 mb-2">
                                        {opt.rule_type === 'pickup' ? 'Date de retrait' : 'Date de livraison souhaitée'}
                                      </p>
                                      <div className="flex flex-wrap gap-2">
                                        {dates.map((dateStr, idx) => (
                                          <button key={idx} onClick={(e) => { e.stopPropagation(); setSelectedDate(dateStr); }}
                                            className={`px-3 py-2 rounded-lg text-[12px] font-medium border transition-all ${selectedDate === dateStr ? 'text-white border-current' : 'border-slate-200 text-slate-600 hover:border-slate-300 bg-white'}`}
                                            style={selectedDate === dateStr ? { backgroundColor: clr.primary, borderColor: clr.primary } : {}}
                                            data-testid={`delivery-date-${idx}`}>
                                            {formatDateFr(dateStr)}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                    {selectedDate && opt.time_slots?.length > 0 && (
                                      <div>
                                        <p className="text-[12px] font-semibold text-slate-600 mb-2">Créneau horaire</p>
                                        <div className="flex flex-wrap gap-2">
                                          {opt.time_slots.map((slot, idx) => (
                                            <button key={idx} onClick={(e) => { e.stopPropagation(); setSelectedSlot(slot); }}
                                              className={`px-3 py-2 rounded-lg text-[12px] font-medium border transition-all ${selectedSlot === slot ? 'text-white' : 'border-slate-200 text-slate-600 hover:border-slate-300 bg-white'}`}
                                              style={selectedSlot === slot ? { backgroundColor: clr.primary, borderColor: clr.primary } : {}}
                                              data-testid={`delivery-slot-${idx}`}>
                                              {slot}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}

                  <div className="flex gap-4 pt-2">
                    <Button variant="outline" onClick={() => setStep(1)}>Retour</Button>
                    <Button 
                      onClick={() => setStep(3)}
                      className="flex-1 btn-primary"
                      disabled={!selectedOption || (selectedOption?.available_dates?.length > 0 && !selectedDate)}
                      data-testid="continue-to-payment"
                    >
                      Continuer
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Payment */}
            {step === 3 && (
              <Card className="dashboard-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard size={20} className="icon-primary" />
                    Paiement
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                    <div className="space-y-3">
                      <label className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover-border" data-testid="payment-stripe">
                        <div className="flex items-center gap-3">
                          <RadioGroupItem value="stripe" id="stripe" />
                          <div>
                            <p className="font-medium">Carte bancaire</p>
                            <p className="text-sm text-slate-500">Visa, Mastercard, Amex</p>
                          </div>
                        </div>
                        <div className="flex gap-1.5 items-center">
                          <div className="h-7 px-2 py-0.5 bg-[#1a1f71] rounded flex items-center"><span className="text-white text-[11px] font-bold italic">VISA</span></div>
                          <div className="h-7 px-1.5 py-0.5 bg-slate-800 rounded flex items-center gap-0.5">
                            <span className="w-3 h-3 rounded-full bg-red-500 -mr-1"></span>
                            <span className="w-3 h-3 rounded-full bg-amber-400 opacity-80"></span>
                          </div>
                          <div className="h-7 px-1.5 py-0.5 bg-white border border-slate-200 rounded flex items-center"><span className="text-[10px] font-bold text-slate-600">CB</span></div>
                        </div>
                      </label>

                      <label className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover-border" data-testid="payment-paypal">
                        <div className="flex items-center gap-3">
                          <RadioGroupItem value="paypal" id="paypal" />
                          <div>
                            <p className="font-medium">PayPal</p>
                            <p className="text-sm text-slate-500">Paiement sécurisé via PayPal</p>
                          </div>
                        </div>
                        <div className="flex gap-1 items-center">
                          <div className="h-7 px-2 py-0.5 bg-[#003087] rounded flex items-center">
                            <span className="text-white text-[11px] font-bold tracking-tight">Pay</span>
                            <span className="text-[#009cde] text-[11px] font-bold tracking-tight">Pal</span>
                          </div>
                        </div>
                      </label>

                      {/* PayPal Pay Later (4x sans frais) simulator — auto-shows when eligible */}
                      <PaypalPayLater
                        amount={(total - (promoApplied?.discount_amount || 0) - (total * (loyaltyDiscount.discount_percent || 0) / 100) + shippingCost) || 0}
                        store={store}
                        variant="block"
                      />

                      {store?.settings?.enable_bank_transfer && (
                        <label className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover-border" data-testid="payment-bank-transfer">
                          <div className="flex items-center gap-3">
                            <RadioGroupItem value="bank_transfer" id="bank_transfer" />
                            <div>
                              <p className="font-medium">Virement bancaire</p>
                              <p className="text-sm text-slate-500">Paiement par virement SEPA</p>
                            </div>
                          </div>
                          <div className="h-7 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded flex items-center">
                            <span className="text-emerald-700 text-[10px] font-bold">SEPA</span>
                          </div>
                        </label>
                      )}

                      {store?.settings?.enable_cod && (
                        <label className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover-border">
                          <div className="flex items-center gap-3">
                            <RadioGroupItem value="cod" id="cod" />
                            <div>
                              <p className="font-medium">Paiement à la livraison</p>
                              <p className="text-sm text-slate-500">Espèces ou carte</p>
                            </div>
                          </div>
                        </label>
                      )}

                      {store?.settings?.enable_pickup && shippingMethod === 'pickup' && (
                        <label className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover-border">
                          <div className="flex items-center gap-3">
                            <RadioGroupItem value="onsite" id="onsite" />
                            <div>
                              <p className="font-medium">Paiement sur place</p>
                              <p className="text-sm text-slate-500">Lors du retrait</p>
                            </div>
                          </div>
                        </label>
                      )}
                    </div>
                  </RadioGroup>

                  <div className="flex gap-4">
                    <Button variant="outline" onClick={() => setStep(2)}>
                      Retour
                    </Button>
                    <Button 
                      onClick={handleSubmit}
                      className="flex-1 btn-primary"
                      disabled={loading}
                      data-testid="place-order"
                    >
                      {loading ? 'Traitement...' : `Payer ${(total - (promoApplied?.discount_amount || 0) + shippingCost).toFixed(2)} €`}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="dashboard-card sticky top-24">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package size={20} />
                  Récapitulatif
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {items.map((item, idx) => (
                  <div key={idx} className="flex gap-3">
                    <div className="w-16 h-16 bg-slate-100 rounded-lg overflow-hidden shrink-0">
                      {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.name}</p>
                      <p className="text-sm text-slate-500">Qté: {item.quantity}</p>
                      <p className="font-semibold">{(item.price * item.quantity).toFixed(2)} €</p>
                    </div>
                  </div>
                ))}

                <Separator />

                {/* Promo Code */}
                <div className="space-y-2" data-testid="promo-code-section">
                  {promoApplied ? (
                    <div className="flex items-center justify-between p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Ticket size={16} className="text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-700">{promoApplied.code}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-emerald-700">-{promoApplied.discount_amount.toFixed(2)} €</span>
                        <button onClick={removePromo} className="text-slate-400 hover:text-red-500 text-xs">Retirer</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Code promo"
                        value={promoCode}
                        onChange={e => setPromoCode(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && validatePromo()}
                        className="text-sm h-9"
                        data-testid="promo-code-input"
                      />
                      <Button size="sm" variant="outline" onClick={validatePromo} disabled={validatingPromo} className="h-9 shrink-0" data-testid="apply-promo-btn">
                        {validatingPromo ? <CircleNotch className="animate-spin" size={14} /> : 'Appliquer'}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Free Shipping Bar */}
                {store?.settings?.free_shipping?.enabled && (() => {
                  const threshold = store.settings.free_shipping.threshold || 80;
                  const remaining = Math.max(0, threshold - total);
                  const progress = Math.min(100, (total / threshold) * 100);
                  const reached = remaining <= 0;
                  return (
                    <div className={`p-2.5 rounded-lg border text-xs ${reached ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'}`} data-testid="checkout-free-shipping">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Truck size={14} className={reached ? 'text-emerald-600' : 'text-blue-600'} />
                        <span className={`font-medium ${reached ? 'text-emerald-700' : 'text-blue-700'}`}>
                          {reached ? (store.settings.free_shipping.message_reached || 'Livraison offerte !') : `Plus que ${remaining.toFixed(2)} € pour la livraison offerte`}
                        </span>
                      </div>
                      <Progress value={progress} className="h-1.5" />
                    </div>
                  );
                })()}

                <Separator />

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Sous-total</span>
                    <span>{total.toFixed(2)} €</span>
                  </div>
                  {promoApplied && (
                    <div className="flex justify-between text-emerald-600">
                      <span>Réduction ({promoApplied.code})</span>
                      <span>-{promoApplied.discount_amount.toFixed(2)} €</span>
                    </div>
                  )}
                  {loyaltyDiscount.discount_percent > 0 && (
                    <div className="flex justify-between text-purple-600" data-testid="loyalty-discount-line">
                      <span className="flex items-center gap-1">
                        <Gift size={14} weight="fill" />
                        Remise {loyaltyDiscount.tier_name} (-{loyaltyDiscount.discount_percent}%)
                      </span>
                      <span>-{(total * loyaltyDiscount.discount_percent / 100).toFixed(2)} €</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Livraison</span>
                    <span>
                      {shippingCost === 0 ? (
                        <span className="text-emerald-600 font-medium">Gratuit</span>
                      ) : (
                        `${shippingCost.toFixed(2)} €`
                      )}
                    </span>
                  </div>
                  {selectedOption && (
                    <div className="text-[11px] text-slate-400">
                      {selectedOption.name}
                      {selectedDate && ` - ${selectedDate}`}
                      {selectedSlot && ` (${selectedSlot})`}
                    </div>
                  )}
                </div>

                {/* VAT breakdown */}
                {taxBreakdown?.groups?.length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-1 text-[12px] text-slate-500" data-testid="vat-breakdown">
                      <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider mb-1">Détail TVA</p>
                      {taxBreakdown.groups.map(g => (
                        <div key={g.rate} className="flex justify-between" data-testid={`vat-row-${g.rate}`}>
                          <span>Dont TVA {String(g.rate).replace('.', ',')}%</span>
                          <span>{g.vat.toFixed(2)} €</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <Separator />

                <div className="flex justify-between font-semibold text-lg">
                  <span>Total</span>
                  <span>
                    {(total - (promoApplied?.discount_amount || 0) - (total * (loyaltyDiscount.discount_percent || 0) / 100) + shippingCost).toFixed(2)} €
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      </main>
      {isMagere && <MagereCheckoutFooter />}
    </div>
  );
};

export default CheckoutPage;
